const DFLOW_BASE_URL = 'https://prediction-markets-api.dflow.net/api/v1'
const KALSHI_API_BASE_URL = 'https://api.elections.kalshi.com/trade-api/v2'

// Build headers for dflow API requests (includes API key if configured)
export function getDflowHeaders(): HeadersInit {
	const headers: HeadersInit = {
		Accept: 'application/json',
	}
	if (process.env.DFLOW_API_KEY) {
		headers['x-api-key'] = process.env.DFLOW_API_KEY
	}
	return headers
}

// Raw API response types
export type DflowMarketAccount = {
	marketLedger: string
	yesMint: string
	noMint: string
	isInitialized: boolean
	redemptionStatus: string | null
}

export type DflowMarket = {
	ticker: string
	eventTicker: string
	marketType: 'binary'
	title: string
	subtitle: string
	yesSubTitle: string

	noSubTitle: string
	openTime: number
	closeTime: number
	expirationTime: number
	// Kalshi market statuses (from official API docs):
	// - initialized: Market created but not yet active
	// - active: Market is open for trading
	// - inactive: Market exists but trading is paused (during tradable hours, treated as "open")
	// - paused: Admin-paused market
	// - closed: Market is closed, awaiting settlement
	// - determined: Outcome determined, settlement pending
	// - disputed: Outcome disputed
	// - amended: Outcome amended after dispute
	// - settled/finalized: Fully settled, funds distributed
	status:
		| 'active'
		| 'initialized'
		| 'inactive'
		| 'paused'
		| 'closed'
		| 'determined'
		| 'disputed'
		| 'amended'
		| 'settled'
		| 'finalized'
	volume: number
	result: string
	openInterest: number
	canCloseEarly: boolean
	earlyCloseCondition: string
	rulesPrimary: string
	rulesSecondary: string
	yesBid: string | null
	yesAsk: string | null
	noBid: string | null
	noAsk: string | null
	accounts: Record<string, DflowMarketAccount>
}

export type DflowEvent = {
	ticker: string
	seriesTicker: string
	strikeDate: string | null
	strikePeriod: string | null
	title: string
	subtitle: string
	imageUrl: string
	competition: string | null
	competitionScope: string | null
	settlementSources: Array<{ name: string; url: string }>
	volume: number
	volume24h: number
	liquidity: number
	openInterest: number
	markets?: DflowMarket[]
}

export type DflowEventsResponse = {
	events: DflowEvent[]
	cursor?: string
}

export type DflowMarketsResponse = {
	markets: DflowMarket[]
	cursor?: string
}

// Series metadata from dflow API
export type DflowSeries = {
	ticker: string
	title: string
	category: string
	tags: string[]
	frequency: string
	settlementSources: Array<{ name: string; url: string }>
	contractUrl: string
	contractTermsUrl: string
	feeType: string
	feeMultiplier: number
	productMetadata: unknown
	additionalProhibitions: string[]
}

export type DflowSeriesListResponse = {
	series: DflowSeries[]
}

// Fetch a single series by ticker (cached 1 hour — series data is static)
export async function fetchDflowSeries(seriesTicker: string): Promise<DflowSeries> {
	const url = `${DFLOW_BASE_URL}/series/${seriesTicker}`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 3600 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch series list with optional filters (cached 1 hour)
export async function fetchDflowSeriesList(params?: {
	category?: string
	tags?: string
	status?: string
	isInitialized?: boolean
}): Promise<DflowSeriesListResponse> {
	const searchParams = new URLSearchParams()

	if (params?.category) {
		searchParams.set('category', params.category)
	}
	if (params?.tags) {
		searchParams.set('tags', params.tags)
	}
	if (params?.status) {
		searchParams.set('status', params.status)
	}
	if (params?.isInitialized !== undefined) {
		searchParams.set('isInitialized', params.isInitialized.toString())
	}

	const url = `${DFLOW_BASE_URL}/series?${searchParams.toString()}`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 3600 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Kalshi status values for filtering
export type KalshiStatusFilter = 'initialized' | 'active' | 'inactive' | 'closed' | 'determined'

// Fetch events with nested markets
export async function fetchDflowEvents(params?: {
	status?: 'active'
	seriesTickers?: string
	limit?: number
	cursor?: string
	withNestedMarkets?: boolean
	sort?: string // volume, volume24h, liquidity, openInterest, startDate
	ascending?: boolean
	isInitialized?: boolean
}): Promise<DflowEventsResponse> {
	const searchParams = new URLSearchParams()

	// Status filter - can be single value or comma-separated list
	if (params?.status === 'active') {
		searchParams.set('status', params.status)
	}
	if (params?.seriesTickers) {
		searchParams.set('seriesTickers', params.seriesTickers)
	}
	if (params?.limit) {
		searchParams.set('limit', params.limit.toString())
	}
	if (params?.cursor) {
		searchParams.set('cursor', params.cursor)
	}
	if (params?.withNestedMarkets !== false) {
		searchParams.set('withNestedMarkets', 'true')
	}
	if (params?.sort) {
		searchParams.set('sort', params.sort)
	}
	if (params?.ascending) {
		searchParams.set('ascending', 'true')
	}
	// Filter by onchain tradability (isInitialized)
	if (params?.isInitialized !== undefined) {
		searchParams.set('isInitialized', params.isInitialized.toString())
	}

	const url = `${DFLOW_BASE_URL}/events?${searchParams.toString()}`

	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 30 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch markets directly (flat list, not grouped by event)
export async function fetchDflowMarkets(params?: {
	status?: KalshiStatusFilter[] | string
	limit?: number
	cursor?: string
	sort?: string // volume, volume24h, liquidity, openInterest, startDate
	ascending?: boolean
	isInitialized?: boolean
}): Promise<DflowMarketsResponse> {
	const searchParams = new URLSearchParams()

	// Status filter - can be single value or comma-separated list
	if (params?.status === 'active') {
		searchParams.set('status', params.status)
	}
	if (params?.limit) {
		searchParams.set('limit', params.limit.toString())
	}
	if (params?.cursor) {
		searchParams.set('cursor', params.cursor)
	}
	if (params?.sort) {
		searchParams.set('sort', params.sort)
	}
	if (params?.ascending) {
		searchParams.set('ascending', 'true')
	}
	// Filter by onchain tradability (isInitialized)
	if (params?.isInitialized !== undefined) {
		searchParams.set('isInitialized', params.isInitialized.toString())
	}

	const url = `${DFLOW_BASE_URL}/markets?${searchParams.toString()}`

	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 30 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch single market by ticker
export async function fetchDflowMarket(ticker: string): Promise<DflowMarket> {
	const url = `${DFLOW_BASE_URL}/market/${ticker}`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 10 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch market by mint address
export async function fetchDflowMarketByMint(mint: string): Promise<DflowMarket> {
	const url = `${DFLOW_BASE_URL}/market/by-mint/${mint}`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 10 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch single event
export async function fetchDflowEvent(ticker: string): Promise<DflowEvent> {
	const url = `${DFLOW_BASE_URL}/event/${ticker}?withNestedMarkets=true`
	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 10 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Kalshi event metadata (team images, colors, settlement sources)
export type KalshiMarketDetail = {
	color_code: string
	image_url: string
	market_ticker: string
}

export type KalshiEventMetadata = {
	competition?: string
	competition_scope?: string
	featured_image_url?: string
	image_url?: string
	market_details?: KalshiMarketDetail[]
	settlement_sources?: Array<{ name: string; url: string }>
}

export type KalshiSportsMarketsResult = {
	gameId: string
	mainEvent: DflowEvent
	eventsByType: Record<string, DflowEvent[]>
	marketTypes: string[]
	totalMarkets: number
	metadata?: KalshiEventMetadata
}

// Title patterns → market type, checked in order (first match wins)
const SERIES_TITLE_PATTERNS: [RegExp, string][] = [
	// Core market types
	[/spread/i, 'spreads'],
	[/\btotal\b|over\/under/i, 'totals'],
	[/both teams to score|btts/i, 'both_teams_to_score'],
	[/map winner/i, 'map_winner'],
	// Player props
	[/touchdown|anytime td|first td|multiple td|2.*td/i, 'touchdowns'],
	[/passing yard/i, 'passing_yards'],
	[/rushing yard/i, 'rushing_yards'],
	[/receiving yard/i, 'receiving_yards'],
	[/passing touchdown/i, 'passing_touchdowns'],
	[/reception/i, 'receptions'],
	// Finishing positions (golf, racing, etc.)
	[/top\s*\d+\s*finisher/i, 'finishing_position'],
	[/round\s*\d+\s*leader/i, 'round_leader'],
	[/make\s*cut|cut\s*line/i, 'make_cut'],
	// Catch-all for game/winner/moneyline
	[/moneyline|winner|game\b/i, 'moneyline'],
]

// Classify a series into a normalized market type using its title.
// Falls back to a cleaned ticker suffix or 'other'.
function classifySeriesMarketType(series: DflowSeries): string {
	const title = series.title

	for (const [pattern, marketType] of SERIES_TITLE_PATTERNS) {
		if (pattern.test(title)) return marketType
	}

	return 'other'
}

// Extract the gameId portion from an event ticker (part after the first dash)
function extractGameId(eventTicker: string): string | null {
	const dashIdx = eventTicker.indexOf('-')
	if (dashIdx === -1) return null
	return eventTicker.substring(dashIdx + 1).toUpperCase()
}

// Fetch all sports markets for a Kalshi event using search-based discovery.
// 1. Fetches the main event
// 2. Searches for companion events by gameId (the part after the dash in the ticker)
// 3. Fetches series metadata for discovered companions to classify market types
export async function fetchKalshiSportsMarkets(
	eventTicker: string
): Promise<KalshiSportsMarketsResult> {
	// Fetch main event and metadata in parallel
	const [mainEvent, metadata] = await Promise.all([
		fetchDflowEvent(eventTicker),
		fetchKalshiEventMetadata(eventTicker),
	])

	const gameId = extractGameId(mainEvent.ticker)
	if (!gameId) {
		return emptyResult(mainEvent.ticker, mainEvent, metadata)
	}

	// Search for all events sharing this gameId (e.g. "ATPBP26" finds all PGA companion events)
	// Note: search endpoint does NOT return nested markets, so we use it for discovery only
	let searchResults: DflowEvent[] = []
	try {
		const response = await fetchDflowSearch({
			q: gameId,
			limit: 50,
			withNestedMarkets: false,
		})
		searchResults = response.events || []
	} catch {
		// Silent — companion discovery is optional
	}

	// Filter to events that actually contain this gameId in their ticker (exclude the main event)
	const companionTickers = searchResults
		.filter(e => {
			const evGameId = extractGameId(e.ticker)
			return evGameId === gameId && e.ticker.toUpperCase() !== mainEvent.ticker.toUpperCase()
		})
		.map(e => e.ticker)

	if (companionTickers.length === 0) {
		return emptyResult(gameId, mainEvent, metadata)
	}

	// Re-fetch each companion event individually to get nested markets + series metadata
	const [eventResults, seriesResults] = await Promise.all([
		// Fetch full events with markets
		Promise.allSettled(companionTickers.map(ticker => fetchDflowEvent(ticker))),
		// Fetch series metadata for classification (deduplicated)
		Promise.allSettled(
			[
				...new Set(
					searchResults.filter(e => companionTickers.includes(e.ticker)).map(e => e.seriesTicker)
				),
			].map(ticker => fetchDflowSeries(ticker))
		),
	])

	// Build series → market type map
	const seriesTypeMap = new Map<string, string>()
	for (const result of seriesResults) {
		if (result.status === 'fulfilled') {
			const series = result.value
			seriesTypeMap.set(series.ticker.toUpperCase(), classifySeriesMarketType(series))
		}
	}

	// Collect successfully fetched companion events and group by market type
	const eventsByType: Record<string, DflowEvent[]> = {}
	for (const result of eventResults) {
		if (result.status !== 'fulfilled') continue
		const event = result.value
		const marketType = seriesTypeMap.get(event.seriesTicker.toUpperCase()) || 'other'
		if (!eventsByType[marketType]) eventsByType[marketType] = []
		eventsByType[marketType].push(event)
	}

	const totalMarkets = Object.values(eventsByType).reduce(
		(sum, events) => sum + events.reduce((s, e) => s + (e.markets?.length || 0), 0),
		0
	)

	return {
		gameId,
		mainEvent,
		eventsByType,
		marketTypes: Object.keys(eventsByType),
		totalMarkets,
		metadata,
	}
}

function emptyResult(
	gameId: string,
	mainEvent: DflowEvent,
	metadata?: KalshiEventMetadata
): KalshiSportsMarketsResult {
	return { gameId, mainEvent, eventsByType: {}, marketTypes: [], totalMarkets: 0, metadata }
}

// Fetch event metadata from Kalshi direct API (team images, colors, settlement sources)
export async function fetchKalshiEventMetadata(
	eventTicker: string
): Promise<KalshiEventMetadata | undefined> {
	try {
		const url = `${KALSHI_API_BASE_URL}/events/${eventTicker}/metadata`
		const res = await fetch(url, {
			headers: { Accept: 'application/json' },
			next: { revalidate: 3600 },
		})
		if (!res.ok) return undefined
		return res.json()
	} catch {
		return undefined
	}
}

// Search endpoint (supports q param)
export async function fetchDflowSearch(params?: {
	q: string
	limit?: number
	cursor?: string
	withNestedMarkets?: boolean
	sort?: string
}): Promise<DflowEventsResponse> {
	const searchParams = new URLSearchParams()

	if (!params?.q) {
		throw new Error('dflow search requires q parameter')
	}

	searchParams.set('q', params.q)

	if (params?.limit) {
		searchParams.set('limit', params.limit.toString())
	}
	if (params?.cursor) {
		searchParams.set('cursor', params.cursor)
	}
	if (params?.withNestedMarkets !== false) {
		searchParams.set('withNestedMarkets', 'true')
	}
	if (params?.sort) {
		searchParams.set('sort', params.sort)
	}

	const url = `${DFLOW_BASE_URL}/search?${searchParams.toString()}`

	const res = await fetch(url, {
		headers: getDflowHeaders(),
		next: { revalidate: 30 },
	})

	if (!res.ok) {
		throw new Error(`dflow API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}
